import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "I suck at implementing neural networks in octave",
  "description": "",
  "published": "2011-11-15T00:00:00.000Z",
  "redirect_from": ["/blog/i-suck-at-implementing-neural-networks-in-octave/swizec/2929"],
  "categories": "Artificial intelligence, Machine learning, Neural network, Octave, Uncategorized",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`A few days ago I implemented my first full `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Neural_network",
        "title": "Neural network"
      }}>{`neural network`}</a>{` in Octave. Nothing too major, just a three layer network recognising hand-written letters. Even though I `}<a parentName="p" {...{
        "href": "http://swizec.com/blog/i-think-i-finally-understand-what-a-neural-network-is/swizec/2891",
        "title": "I think I finally understand what a neural network is"
      }}>{`finally understood what a neural network is`}</a>{`, this was still a cool challenge.`}</p>
    <p>{`Yes, even despite having so much support from ml-class ... they practically implement everything and just leave the cost and gradient functions up to you to implement. Then again, Octave provides tools for learning where you essentially just run a function, tell it where to find the cost and gradient function and give it some data.`}</p>
    <p>{`Then the magic happens.`}</p>
    <p>{`Getting the basic implementation to work is really simple since the formulas being used aren't all that complex:`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2011/11/Screen-Shot-2011-11-16-at-1.07.26-AM.png",
        "alt": "Neural network cost function",
        "title": "Neural network cost function"
      }}></img></p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2011/11/Screen-Shot-2011-11-16-at-1.08.17-AM.png",
        "alt": "Neural network backpropagation",
        "title": "Neural network backpropagation"
      }}></img></p>
    <p>{`Here's the code I've come up with to get this working on a three layer network:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-matlab"
      }}>{`function [J grad] = nnCostFunction(nn_params, ...
                                   input_layer_size, ...
                                   hidden_layer_size, ...
                                   num_labels, ...
                                   X, y, lambda)
%NNCOSTFUNCTION Implements the neural network cost function for a two layer
%neural network which performs classification
%   [J grad] = NNCOSTFUNCTON(nn_params, hidden_layer_size, num_labels, ...
%   X, y, lambda) computes the cost and gradient of the neural network. The
%   parameters for the neural network are "unrolled" into the vector
%   nn_params and need to be converted back into the weight matrices.
%
%   The returned parameter grad should be a "unrolled" vector of the
%   partial derivatives of the neural network.
%

% Reshape nn_params back into the parameters Theta1 and Theta2, the weight matrices
% for our 2 layer neural network
Theta1 = reshape(nn_params(1:hidden_layer_size * (input_layer_size + 1)), ...
                 hidden_layer_size, (input_layer_size + 1));

Theta2 = reshape(nn_params((1 + (hidden_layer_size * (input_layer_size + 1))):end), ...
                 num_labels, (hidden_layer_size + 1));

% Setup some useful variables
m = size(X, 1);

% You need to return the following variables correctly
J = 0;
Theta1_grad = zeros(size(Theta1));
Theta2_grad = zeros(size(Theta2));

yy = zeros(size(y),num_labels);
for i=1:size(X)
  yy(i,y(i)) = 1;
end

X = [ones(m,1) X];
% cost
for  i=1:m
  a1 = X(i,:);
  z2 = Theta1*a1';
  a2 = sigmoid(z2);
  z3 = Theta2*[1; a2];
  a3 = sigmoid(z3);

  J += -yy(i,:)*log(a3)-(1-yy(i,:))*log(1-a3);
end

J /= m;

J += (lambda/(2*m))*(sum(sum(Theta1(:,2:end).^2))+sum(sum(Theta2(:,2:end).^2)));

t=1;
for t=1:m
  % forward pass
  a1 = X(t,:);
  z2 = Theta1*a1';
  a2 = [1; sigmoid(z2)];
  z3 = Theta2*a2;
  a3 = sigmoid(z3);

  % backprop
  delta3 = a3-yy(t,:)';
  delta2 = (Theta2'*delta3).*[1; sigmoidGradient(z2)];
  delta2 = delta2(2:end);

  Theta1_grad = Theta1_grad + delta2*a1;
  Theta2_grad = Theta2_grad + delta3*a2';
end

Theta1_grad = (1/m)*Theta1_grad+(lambda/m)*[zeros(size(Theta1, 1), 1) Theta1(:,2:end)];
Theta2_grad = (1/m)*Theta2_grad+(lambda/m)*[zeros(size(Theta2, 1), 1) Theta2(:,2:end)];

% Unroll gradients
grad = [Theta1_grad(:) ; Theta2_grad(:)];

end
`}</code></pre>
    <p>{`This then basically gets pumped into the `}<em parentName="p">{`fmincg`}</em>{` function and on the other end a result pops out.`}</p>
    <p>{`Now, I've managed to vectorize this thing to the edge of my capabilities. But I know it's still just matrix multiplication so I know for a fact it should be possible to vectorize even further. Anyone know how to do that?`}</p>
    <p>{`Also, if you know of a cool way to generalize the algorithm so it would work on bigger networks, I'd love to hear about that as well!`}</p>
    <h6 {...{
      "id": "related-articles"
    }}>{`Related articles`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://joeyjardins.wordpress.com/2011/10/22/unique-neural-networks-make-leo-trader-pro-a-unique-and-profitable-forex-trading-system/"
        }}>{`Unique Neural Networks Make Leo Trader Pro A Unique And Profitable Forex Trading System`}</a>{` (joeyjardins.wordpress.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://heuristically.wordpress.com/2011/11/11/train-neural-network-in-r-predict-in-sas/"
        }}>{`Train neural network in R, predict in SAS`}</a>{` (heuristically.wordpress.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://vql89.wordpress.com/2011/10/26/training-of-neural-network/"
        }}>{`Training of Neural Network`}</a>{` (vql89.wordpress.com)`}</li>
    </ul>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=2cd7a0e7-5dea-4e23-8cd7-086d494a8ef1",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      